import { apiClient, type ApiResponse } from './client';
import type { TrainingSession } from '../types';
import type { TrainingSetCreateRequest, TrainingSet, TrainingProjection, UpdateTrainingRequest, TrainingSetDetailsResponse, ThisWeekTraining, TrainingSetWithUser } from '../types/api';

export interface CreateSessionDto {
  traineeId: string;
  date: string;
  completed: boolean;
  notes?: string;
  duration?: number;
}

export interface UpdateSessionDto {
  date?: string;
  completed?: boolean;
  notes?: string;
  duration?: number;
}

export const trainingApi = {
  /**
   * Get all training sessions
   */
  async getAll(): Promise<TrainingSession[]> {
    const response = await apiClient.get<ApiResponse<TrainingSession[]>>('/sessions');
    return response.data;
  },

  /**
   * Get sessions for a specific trainee
   */
  async getByTrainee(traineeId: string): Promise<TrainingSession[]> {
    const response = await apiClient.get<ApiResponse<TrainingSession[]>>(`/trainees/${traineeId}/sessions`);
    return response.data;
  },

  /**
   * Get a single session by ID
   */
  async getById(id: string): Promise<TrainingSession> {
    const response = await apiClient.get<ApiResponse<TrainingSession>>(`/sessions/${id}`);
    return response.data;
  },

  /**
   * Create a new training session
   */
  async create(data: CreateSessionDto): Promise<TrainingSession> {
    const response = await apiClient.post<ApiResponse<TrainingSession>>('/sessions', data);
    return response.data;
  },

  /**
   * Update an existing session
   */
  async update(id: string, data: UpdateSessionDto): Promise<TrainingSession> {
    const response = await apiClient.put<ApiResponse<TrainingSession>>(`/sessions/${id}`, data);
    return response.data;
  },

  /**
   * Delete a session
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`/sessions/${id}`);
  },

  /**
   * Mark session as completed
   */
  async markCompleted(id: string): Promise<TrainingSession> {
    return this.update(id, { completed: true });
  },

  /**
   * Create a new training set for a trainee
   */
  async createTrainingSet(request: TrainingSetCreateRequest): Promise<void> {
    return apiClient.post('/v1/training/set/create', request);
  },

  /**
   * Get all training sets for a trainee
   */
  async getTraineeSets(traineeId: number): Promise<TrainingSet[]> {
    return apiClient.get<TrainingSet[]>(`/v1/training/trainee/${traineeId}/sets`);
  },

  /**
   * Get all training sets for a coach
   */
  async getCoachSets(coachId: number): Promise<TrainingSet[]> {
    return apiClient.get<TrainingSet[]>(`/v1/training/coach/${coachId}/sets`);
  },

  /**
   * Get preview of training projections before creating a set
   */
  async getTrainingPreview(request: TrainingSetCreateRequest): Promise<TrainingProjection[]> {
    return apiClient.post<TrainingProjection[]>('/v1/training/projections/preview', request);
  },

  /**
   * Get all trainings for a specific training set
   */
  async getSetTrainings(traineeId: number, setId: number): Promise<TrainingProjection[]> {
    return apiClient.get<TrainingProjection[]>(`/v1/training/trainee/${traineeId}/set/${setId}`);
  },

  /**
   * Get training set details with related user and projections
   */
  async getTrainingSetDetails(setId: number): Promise<TrainingSetDetailsResponse> {
    return apiClient.get<TrainingSetDetailsResponse>(`/v1/training/set/${setId}`);
  },

  /**
   * Update a training projection
   */
  async updateTraining(setId: number, projectionId: number, request: UpdateTrainingRequest): Promise<void> {
    return apiClient.patch(`/v1/training/set/${setId}/projections/${projectionId}`, request);
  },

  /**
   * Delete a training set
   */
  async deleteTrainingSet(traineeId: number, setId: number): Promise<void> {
    return apiClient.delete(`/v1/training/trainee/${traineeId}/set/${setId}`);
  },

  /**
   * Complete a training set
   */
  async completeTrainingSet(traineeId: number, setId: number, autoProlongation?: boolean): Promise<void> {
    const params = autoProlongation ? '?autoProlongation=true' : '';
    return apiClient.patch(`/v1/training/trainee/${traineeId}/set/${setId}/complete${params}`, {});
  },

  /**
   * Get trainings in date range
   */
  async getTrainingsInRange(from: string, to: string): Promise<ThisWeekTraining[]> {
    return apiClient.get<ThisWeekTraining[]>(`/v1/training/range?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  },

  /**
   * Get all training sets for the current user with related user information.
   * Returns ACTIVE sets plus COMPLETED sets marked unpaid for coach or trainee.
   */
  async getAllTrainingSets(): Promise<TrainingSetWithUser[]> {
    return apiClient.get<TrainingSetWithUser[]>(`/v1/training/sets/all`);
  },

  /**
   * Mark or clear the unpaid attention flag on a training set (coach only)
   */
  async updateTrainingSetUnpaid(setId: number, unpaid: boolean): Promise<void> {
    return apiClient.patch(`/v1/training/set/${setId}/unpaid`, { unpaid });
  },

  /**
   * Pause a training set
   */
  async pauseTrainingSet(setId: number, pauseStartDate: string, pauseEndDate: string): Promise<void> {
    return apiClient.post(`/v1/training/set/${setId}/pause`, {
      pauseStartDate,
      pauseEndDate,
    });
  },

  /**
   * Resume a paused training set
   */
  async resumeTrainingSet(setId: number): Promise<void> {
    return apiClient.post(`/v1/training/set/${setId}/resume`, {});
  },

  /**
   * Cancel a pause by deleting projections generated during pause and recreating from pause start date
   */
  async cancelPause(setId: number): Promise<void> {
    return apiClient.post(`/v1/training/set/${setId}/cancel-pause`, {});
  },
};
